<template>
  <div>
    <Header />

    <div id="back-button">
      <router-link :to="{ name: 'Data Listing'}" tag="span">&larr; Back</router-link>
    </div>

    <div id="data-graph-container" :class="{ 'toggle-screenshot': inScreenshotMode }">
      <div id="data-header">
        <h1 id="data-title">{{ dataLabel }}</h1>
        <h2 id="data-subtitle">{{ dataCategory }}</h2>
        <b class="series-id-text" v-if="!inScreenshotMode">Data #{{dataID}}</b>
      </div>

      <div v-if="loaded" id="data-view">
        <TimeSeriesGraph
          :graphWidth="optimalGraphWidth"
          :datas="data"
          :showTitle="false"
          :showLegend="false"
        />
      </div>
    </div>

    <div id="description">
      <b>Description</b>
      <p>{{ dataDescription }}</p>
    </div>

    <!-- Hidden Button to Toggle Screenshots -->
    <button @click="inScreenshotMode = !inScreenshotMode"
      id="screenshotButton"
      style="display: none;">
    </button>
    <!----------------------------------------->

    <hr />

    <EmailCollection />

    <Footer />
  </div>
</template>

<script>
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import TimeSeriesGraph from '@/components/model/TimeSeriesGraph';
import EmailCollection from '@/components/layout/EmailCollection';

import { TimeSeries } from '@/js/model/DataSeries';
import { loadData } from '@/js/model/Storage';

const axios = require('axios');
const _ = require('lodash');

export default {
  name: 'DataView',
  components: {
    Header,
    Footer,
    TimeSeriesGraph,
    EmailCollection
  },
  props: {
    dataID: Number
  },
  data() {
    return {
      loaded: false,
      data: null,
      dataLabel: '',
      dataDescription: '',
      dataCategory: '',
      inScreenshotMode: false
    }
  },
  metaInfo() {
    return {
      title: `${this.dataLabel} (${this.dataCategory})`,
      meta: [
        // Title
        {'vmid': 'twitter:title', 'name': 'twitter:title', 'content': `${this.minorCategory}'s ${this.dataLabel} on Rebar`},
        {'vmid': 'og:title', 'property': 'og:title', 'content': `${this.minorCategory}'s ${this.dataLabel} on Rebar`},

        // Image
        {'vmid': 'twitter:image', 'name': 'twitter:image', 'content': `https://www.rebar.ai/images/data/${this.dataID}.png`},
        {'vmid': 'og:image', 'property': 'og:image', 'content': `https://www.rebar.ai/images/data/${this.dataID}.png`},

        // Description
        {'vmid': 'twitter:description', 'name': 'twitter:description',
         'content': `View ${this.minorCategory}'s ${this.dataLabel} and other data for ${this.majorCategory} on Rebar`},
        {'vmid': 'og:description', 'property': 'og:description',
         'content': `View ${this.minorCategory}'s ${this.dataLabel} and other data for ${this.majorCategory} on Rebar`},
        
        // Canonical URL
        {'vmid': 'og:url', 'property': 'og:url', 'content': `https://www.rebar.ai/data/${this.dataID}`},
      ]
    }
  },
  created() {
    this.loadRemoteData();
  },
  watch: {
    dataID: function() {
      this.loadRemoteData();
    }
  },
  computed: {
    optimalGraphWidth: function() {
      return Math.max(400, Math.min(850, window.innerWidth));
    },
    majorCategory: function() {
      return this.dataCategory ? this.dataCategory.split('/')[0].trim() : '';
    },
    minorCategory: function() {
      return this.dataCategory ? this.dataCategory.split('/')[1].trim() : '';
    }
  },
  methods: {
    loadRemoteData() {
      axios.get(`${this.$api}/data/${this.dataID}`)
        .then((response) => {
          let responseData = (response.data || {});

          let theData = loadData(responseData.Data);

          this.dataLabel = responseData.Label;
          this.dataDescription = responseData.Description;
          this.dataCategory = responseData.Category.split('#').join(' / ');
          this.data = new TimeSeries(theData);

          this.loaded = true;
        })
        .catch(() => console.log("Error loading data"));
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
#back-button {
  display: flex;
  justify-content: left;
  padding: 10px 0 0 10px;
}
#back-button span {
  font-weight: bold;
  padding: 5px 10px;
  background-color: #00bc83;
  border: solid 1px black;
  border-radius: 3px;
  color: white;
}

/* Data Header */
#data-header > * {
  margin: 0px;
}
#data-title {
  font-size: 1.3em;
}
#data-subtitle {
  font-size: 1em;
  font-weight: normal;
}
.series-id-text {
  color: gray;
  font-size: .8em;
}

/* Data Graph */
#data-view {
  margin-left: auto;
  margin-right: auto;
  max-width: 90%;
}

/* Data Description */
#description {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 50px;
  padding-bottom: 30px;
}
#description p {
  width: 75%;
}

/* Screenshot Model */
.toggle-screenshot {
  color: white;
  background-color: #0666B4;

  height: 235px;
  width: 450px;

  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
}
.toggle-screenshot #data-title {
  font-size: 0.9em;
}
.toggle-screenshot #data-subtitle {
  font-size: 0.9em;
}
.toggle-screenshot > #data-view {
  width: 95%;
  max-width: 95%;
  margin: 0;
  padding-bottom: 10px;
}
.toggle-screenshot > #data-logo {
  margin: 0;
  padding-right: 50px;
  width: 100%;
  text-align: end;
}
.toggle-screenshot >>> .actuals-line {
  stroke: white;
}

/* Adjust for Mobile */
@media only screen and (max-width: 800px) {
  #back-button span {
    font-size: .8em;
  }
}
</style>
